import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Grid, SkeletonElement, Ratings } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { GetLoadingProductQuery } from '@notino/shared/definitions/types';

import { ContentWrapper, TabWrapper } from '@components';
import { TabSwitcherProvider } from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';
import { TabDivider } from '@sharedComponents/TabSwitcher/styled';

import messages from '../messages';
import { BreadcrumbWrapper } from '../ProductDetail/Breadcrumb/styled';
import { Header } from '../ProductDetail/Header';
import { useNewPdDesignEnabled } from '../ProductDetail/hooks/useNewPdDesignEnabled';
import {
  CodeBlock,
  CodeName,
  Separator,
  Availability,
  Wrapper,
} from '../ProductDetail/ProductBaseInfo/BellowTheLine/styled';
import { Container } from '../ProductDetail/ProductBaseInfo/styled';
import { ProductImageGallery } from '../ProductDetail/ProductImageGallery';
import { ContentTabLink } from '../ProductDetail/ProductTabContent/ContentSwitcher/ContentTabLink';
import {
  ContentSwitcherWrapper,
  TabList,
  ContentSwitcherContainer,
  Container as SwitcherContainer,
} from '../ProductDetail/ProductTabContent/ContentSwitcher/styled';
import {
  Main,
  TopSection,
  GalleryWrapper,
  ShowOnMobile,
  MainInfoWrapper,
  ShowOnDesktop,
} from '../ProductDetail/styled';

import { PDSkeletonElement, EmptySpace, Row } from './styled';

interface ILoadingSkeletonProps {
  product: GetLoadingProductQuery['vpProductById'];
  imageDomainPath: string;
}

export const SpaLoadingSkeleton = ({
  product,
  imageDomainPath,
}: ILoadingSkeletonProps) => {
  const newPdDesign = useNewPdDesignEnabled();

  const headerProps = {
    brandInfo: {
      id: '#',
      name: '',
      __typename: 'Brand' as const,
      description: '',
      url: '#',
      market: '',
      manufacturer: '',
    },
    productId: product.id,
    catalogId: product.catalogMasterId.toString(),
    productCode: product.productCode,
    collection: product.name,
    subName: null,
    desc: product.annotation,
    hideReviews: true,
  };

  const ratingsProps = {
    rating: product?.reviewOverview?.rating ?? 0,
    size: 16,
    numReviews: 0,
    foreGround: 'text.highlight',
    microdata: false,
  };

  return (
    <>
      <ContentWrapper data-testid="pd-loading-skeleton" isWider={newPdDesign}>
        <Main>
          <Grid fluid={true}>
            <TopSection newPdDesign={newPdDesign} isLoading={true}>
              <GalleryWrapper newPdDesignEnabled={newPdDesign}>
                <ShowOnMobile newPdDesign={newPdDesign}>
                  <BreadcrumbWrapper newPdDesign={newPdDesign}>
                    <SkeletonElement height={1} width={15} />
                  </BreadcrumbWrapper>
                  <Header {...headerProps}>
                    <Ratings
                      ratingId="loading-skeleton-id-mobile"
                      {...ratingsProps}
                    />
                  </Header>
                </ShowOnMobile>

                <ProductImageGallery
                  media={{
                    mainImage: {
                      src: product.imageUrl,
                      alt: `${product?.brand?.name} ${product?.name}`,

                      __typename: 'Image',
                    },
                    __typename: 'VariantMedia',
                    shadeImage: null,
                    previewImage: null,
                    others: [],
                    videos: [],
                  }}
                  domain={imageDomainPath}
                />
              </GalleryWrapper>

              <MainInfoWrapper newPdDesignEnabled={newPdDesign}>
                <ShowOnDesktop newPdDesign={newPdDesign}>
                  <BreadcrumbWrapper newPdDesign={newPdDesign}>
                    <SkeletonElement height={1} width={15} />
                  </BreadcrumbWrapper>
                  <Header {...headerProps}>
                    <Ratings
                      ratingId="loading-skeleton-id-desktop"
                      {...ratingsProps}
                    />
                  </Header>
                </ShowOnDesktop>
                <Container newDesign={newPdDesign}>
                  <PDSkeletonElement
                    height={2}
                    marginTop={7.5}
                    marginBottom={0.5}
                  />

                  <Row>
                    <PDSkeletonElement
                      width={6.8}
                      height={3.5}
                      marginRight={0.25}
                    />
                    <PDSkeletonElement
                      width={6.8}
                      height={3.5}
                      marginRight={0.25}
                    />
                    <PDSkeletonElement
                      width={6.8}
                      height={3.5}
                      marginRight={0.25}
                    />
                  </Row>
                  <Wrapper newDesign={newPdDesign}>
                    <CodeBlock>
                      <CodeName>
                        <FormattedMessage {...messages.code} />{' '}
                      </CodeName>
                      <PDSkeletonElement
                        width={4}
                        height={1}
                        display="inline-block"
                      />
                    </CodeBlock>

                    <Separator newDesign={newPdDesign}> | </Separator>
                    <Availability
                      newDesign={newPdDesign}
                      highlight={[IStock.outOfStock, IStock.lessThan5].includes(
                        product.stockAvailability.code as IStock
                      )}
                    >
                      <FormattedMessage
                        {...messages[product.stockAvailability.code]}
                        values={{ count: product.stockAvailability.count }}
                      />
                    </Availability>
                  </Wrapper>
                  <PDSkeletonElement width={20} height={3} marginTop={2} />
                  <PDSkeletonElement width={16} height={2} marginTop={1} />
                  <PDSkeletonElement height={4} marginTop={2} />

                  <PDSkeletonElement height={2} marginTop={2} />
                </Container>
              </MainInfoWrapper>
            </TopSection>

            <TabSwitcherProvider>
              <>
                <ContentSwitcherContainer newPdDesignEnabled={newPdDesign}>
                  <ContentSwitcherWrapper>
                    <SwitcherContainer newPdDesignEnabled={newPdDesign}>
                      <Row>
                        <TabList>
                          <ContentTabLink
                            tabId={1}
                            isDefaultTab={true}
                            id="tabDescription"
                          >
                            <FormattedMessage
                              {...messages.prodDescription}
                              values={{ name: '' }}
                            />
                          </ContentTabLink>
                        </TabList>
                      </Row>
                    </SwitcherContainer>
                  </ContentSwitcherWrapper>
                </ContentSwitcherContainer>
                <TabWrapper newPdDesignEnabled={newPdDesign} role="tabpanel">
                  <div>
                    <TabDivider
                      newPdDesignEnabled={newPdDesign}
                      tabDividerMobile={true}
                      tabDividerDesktop={true}
                    />
                  </div>
                </TabWrapper>
              </>
            </TabSwitcherProvider>
          </Grid>
        </Main>
      </ContentWrapper>
      <EmptySpace />
    </>
  );
};
